import React, { useEffect, useMemo, useState } from 'react';
import {
  Search, Plus, Edit2, Trash2, X, Save, ArrowLeft,
  Laptop, Monitor, Smartphone, Cable, Package, ChevronRight,
  type LucideIcon, PcCase,
} from 'lucide-react';
import { useApi } from '../auth/useApi';
import { SearchableSelect } from '../components/SearchableSelect';
import { AssetImportPanel } from '../components/AssetImportPanel';
import { SoftwareImportPanel } from '../components/SoftwareImportPanel';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

const STATUS_COLORS: Record<string, string> = {
  'in-use':  C.ok,
  'spare':   C.secondary,
  'repair':  C.warn,
  'retired': C.textMuted,
};

const ASSET_TYPE_ICONS: Record<string, LucideIcon> = {
  laptop: Laptop,
  desktop: PcCase,
  monitor: Monitor,
  phone: Smartphone,
  dock: Cable,
  other: Package,
};

const ASSET_TYPES = ['laptop', 'desktop', 'monitor', 'phone', 'dock', 'other'];
const STATUSES = ['in-use', 'spare', 'repair', 'retired'];

interface Me {
  userId: number;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface Asset {
  AssetId: number;
  AssetTag: string;
  Name: string;
  AssetType: string;
  SerialNumber: string | null;
  WarrantyEndDate: string | null;
  AssignedUserId: number | null;
  AssignedToName: string | null;
  Status: string;
  Notes: string | null;
  PurchasedAt: string | null;
  CreatedAt: string;
}

interface Assignment {
  AssignmentId: number;
  AssetId: number;
  UserId: number;
  DisplayName: string;
  AssignedAt: string;
  UnassignedAt: string | null;
}

interface User { UserId: number; DisplayName: string; Email: string; }

// ============================================================================
// Assets Page
// ============================================================================
export const Assets: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [editing, setEditing] = useState<Asset | null>(null);
  const [creating, setCreating] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('active'); // hides retired by default
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all'); // all | assigned | unassigned
  const [tab, setTab] = useState<'devices' | 'software'>('devices');

  const canEdit = me.role === 'admin' || me.role === 'tech';
  const canDelete = me.role === 'admin' || me.role === 'tech';

  const load = async () => {
    setLoading(true);
    try {
      const [a, u] = await Promise.all([
        api.get<Asset[]>('/api/assets'),
        api.get<User[]>('/api/users'),
      ]);
      setAssets(Array.isArray(a) ? a : []);
      setUsers(Array.isArray(u) ? u : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let out = assets;
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(a =>
        a.AssetTag.toLowerCase().includes(s) ||
        a.Name.toLowerCase().includes(s) ||
        (a.SerialNumber ?? '').toLowerCase().includes(s) ||
        (a.AssignedToName ?? '').toLowerCase().includes(s)
      );
    }
    if (typeFilter !== 'all') out = out.filter(a => a.AssetType === typeFilter);
    if (statusFilter === 'active') out = out.filter(a => a.Status !== 'retired');
    else if (statusFilter !== 'all') out = out.filter(a => a.Status === statusFilter);
    if (assigneeFilter === 'assigned')   out = out.filter(a => a.AssignedUserId !== null);
    if (assigneeFilter === 'unassigned') out = out.filter(a => a.AssignedUserId === null);
    return out;
  }, [assets, search, typeFilter, statusFilter, assigneeFilter]);

  const handleDelete = async (a: Asset) => {
    if (!window.confirm(`Retire asset "${a.AssetTag} — ${a.Name}"? It will be marked as retired (not deleted).`)) return;
    try {
      await api.del(`/api/assets/${a.AssetId}`);
      if (selectedAssetId === a.AssetId) setSelectedAssetId(null);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  // KPI counts (from full list, before filters)
  const counts = useMemo(() => {
    const active = assets.filter(a => a.Status !== 'retired');
    return {
      total: active.length,
      inUse: assets.filter(a => a.Status === 'in-use').length,
      spare: assets.filter(a => a.Status === 'spare').length,
      repair: assets.filter(a => a.Status === 'repair').length,
      unassigned: active.filter(a => a.AssignedUserId === null).length,
    };
  }, [assets]);

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading assets…</div>;
  if (error) return <div style={{ padding: 40, color: C.danger }}>Failed to load: {error}</div>;

  const TabBar = () => (
    <div style={{ display: 'flex', gap: 4, marginBottom: 18, borderBottom: `1px solid ${C.border}` }}>
      {(['devices'] as const).map(t => (
        <button key={t} onClick={() => { setTab(t); setSelectedAssetId(null); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px',
            fontSize: 14, fontWeight: 600, textTransform: 'capitalize',
            color: tab === t ? C.primary : C.textMuted,
            borderBottom: tab === t ? `2px solid ${C.primary}` : '2px solid transparent',
            marginBottom: -1,
          }}>
          {t}
        </button>
      ))}
    </div>
  );


  // Detail view
  if (selectedAssetId !== null) {
    const asset = assets.find(a => a.AssetId === selectedAssetId);
    if (!asset) {
      setSelectedAssetId(null);
      return null;
    }
    return (
      <AssetDetail
        asset={asset}
        users={users}
        canEdit={canEdit}
        canDelete={canDelete}
        onBack={() => setSelectedAssetId(null)}
        onEdit={() => { setSelectedAssetId(null); setEditing(asset); }}
        onDelete={() => handleDelete(asset)}
        api={api}
      />
    );
  }

  return (
    <div>
      <h1 style={{ color: C.primary, fontSize: 26, margin: '0 0 14px' }}>Hardware</h1>
      <TabBar />
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 18 }}>
        <div>
          <p style={{ color: C.textMuted, margin: 0 }}>
            {filtered.length} of {counts.total} active {counts.total === 1 ? 'asset' : 'assets'}
          </p>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{
              background: 'transparent', color: C.primary, border: `1px solid ${C.border}`, padding: '8px 14px',
              borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }} onClick={() => setShowImport(v => !v)}>
              Import CSV
            </button>
            <button style={{
              background: C.primary, color: '#fff', border: 'none', padding: '8px 14px',
              borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }} onClick={() => setCreating(true)}>
              <Plus size={14} /> New Asset
            </button>
          </div>
        )}
      </div>

      {canEdit && showImport && (
        <AssetImportPanel api={api} onClose={() => setShowImport(false)} onChanged={load} />
      )}

      {/* KPI row */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16,
      }}>
        <KPICard label="In Use" value={counts.inUse} color={C.ok} />
        <KPICard label="Spare" value={counts.spare} color={C.secondary} />
        <KPICard label="In Repair" value={counts.repair} color={C.warn} />
        <KPICard label="Unassigned" value={counts.unassigned} color={C.textMuted} />
      </div>

      {/* Filters */}
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: 16, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center',
      }}>
        <div style={{ flex: '1 1 220px', position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.textMuted }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search tag, name, serial, assignee…"
            style={{
              border: `1px solid ${C.border}`, padding: '8px 10px 8px 30px',
              borderRadius: 6, fontSize: 14, width: '100%', boxSizing: 'border-box', background: C.bg,
            }}
          />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={filterSty()}>
          <option value="all">All Types</option>
          {ASSET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={filterSty()}>
          <option value="active">Active</option>
          <option value="all">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} style={filterSty()}>
          <option value="all">All</option>
          <option value="assigned">Assigned</option>
          <option value="unassigned">Unassigned</option>
        </select>
      </div>

      {/* List */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
            No assets match the current filters.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: C.bg }}>
              <tr>
                <th style={th()}>Type</th>
                <th style={th()}>Tag</th>
                <th style={th()}>Name</th>
                <th style={th()}>Serial</th>
                <th style={th()}>Assigned To</th>
                <th style={th()}>Status</th>
                <th style={th()}>Warranty</th>
                <th style={th()}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => {
                const Icon = ASSET_TYPE_ICONS[a.AssetType] ?? Package;
                const warrantyDays = a.WarrantyEndDate ? daysUntil(a.WarrantyEndDate) : null;
                return (
                  <tr
                    key={a.AssetId}
                    onClick={() => setSelectedAssetId(a.AssetId)}
                    style={{
                      borderTop: `1px solid ${C.border}`,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = C.bg)}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={td()}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: C.textMuted }}>
                        <Icon size={14} />
                        <span style={{ fontSize: 11, textTransform: 'capitalize' }}>{a.AssetType}</span>
                      </div>
                    </td>
                    <td style={{ ...td(), fontFamily: 'monospace', fontWeight: 600, color: C.primary }}>{a.AssetTag}</td>
                    <td style={td()}>{a.Name}</td>
                    <td style={{ ...td(), color: C.textMuted, fontFamily: 'monospace', fontSize: 12 }}>
                      {a.SerialNumber ?? <span style={{ fontStyle: 'italic' }}>—</span>}
                    </td>
                    <td style={td()}>
                      {a.AssignedToName ?? <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Unassigned</span>}
                    </td>
                    <td style={td()}>
                      <Badge color={STATUS_COLORS[a.Status] ?? C.textMuted}>{a.Status}</Badge>
                    </td>
                    <td style={{ ...td(), fontSize: 12 }}>
                      {a.WarrantyEndDate ? (
                        <span style={{
                          color: warrantyDays! < 0 ? C.danger : warrantyDays! < 30 ? C.warn : C.textMuted,
                        }}>
                          {warrantyDays! < 0 ? 'Expired' : warrantyDays! < 30 ? `${warrantyDays}d left` : new Date(a.WarrantyEndDate).toLocaleDateString()}
                        </span>
                      ) : <span style={{ color: C.textMuted }}>—</span>}
                    </td>
                    <td style={{ ...td(), textAlign: 'right', color: C.textMuted }}>
                      <ChevronRight size={14} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {(editing || creating) && (
        <AssetModal
          asset={editing}
          users={users}
          onSave={async (data) => {
            try {
              if (editing) {
                await api.patch(`/api/assets/${editing.AssetId}`, data);
              } else {
                await api.post('/api/assets', data);
              }
              setEditing(null); setCreating(false);
              await load();
            } catch (e: any) { alert(`Failed: ${e?.message}`); }
          }}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
    </div>
  );
};

// ============================================================================
// Detail View
// ============================================================================
const AssetDetail: React.FC<{
  asset: Asset;
  users: User[];
  canEdit: boolean;
  canDelete: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  api: any;
}> = ({ asset, users: _users, canEdit, canDelete, onBack, onEdit, onDelete, api }) => {
  const [history, setHistory] = useState<Assignment[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [linkedTickets, setLinkedTickets] = useState<any[]>([]);
  const [allTickets, setAllTickets] = useState<any[]>([]);
  const [ticketPick, setTicketPick] = useState<number | ''>('');
  const [linkBusy, setLinkBusy] = useState(false);
  const Icon = ASSET_TYPE_ICONS[asset.AssetType] ?? Package;

  const loadTickets = async () => {
    try {
      const { tickets } = await api.get(`/api/assets/${asset.AssetId}/tickets`);
      setLinkedTickets(Array.isArray(tickets) ? tickets : []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const { history } = await api.get(`/api/assets/${asset.AssetId}`);
        setHistory(Array.isArray(history) ? history : []);
      } catch { /* ignore */ }
      finally { setLoadingHistory(false); }
    })();
    loadTickets();
  // eslint-disable-next-line
  }, [asset.AssetId]);

  const loadAllTickets = async () => {
    if (allTickets.length) return;
    try { const t = await api.get('/api/tickets'); setAllTickets(Array.isArray(t) ? t : []); } catch { /* ignore */ }
  };
  const linkTicket = async () => {
    if (!ticketPick) return;
    setLinkBusy(true);
    try { await api.post(`/api/tickets/${ticketPick}/assets`, { assetId: asset.AssetId }); setTicketPick(''); await loadTickets(); }
    catch (e: any) { alert(`Failed to link ticket: ${e?.message ?? 'error'}`); }
    finally { setLinkBusy(false); }
  };
  const unlinkTicket = async (ticketId: number) => {
    setLinkBusy(true);
    try { await api.del(`/api/tickets/${ticketId}/assets/${asset.AssetId}`); await loadTickets(); }
    catch (e: any) { alert(`Failed to unlink ticket: ${e?.message ?? 'error'}`); }
    finally { setLinkBusy(false); }
  };

  return (
    <div>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', color: C.primary,
        fontSize: 13, cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', gap: 4, padding: 0, marginBottom: 12,
      }}>
        <ArrowLeft size={14} /> Back to assets
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 10, background: `${C.primary}15`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.primary,
          }}>
            <Icon size={28} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: C.textMuted, fontFamily: 'monospace', textTransform: 'uppercase' }}>
              {asset.AssetTag} · {asset.AssetType}
            </div>
            <h1 style={{ color: C.text, fontSize: 22, margin: '4px 0 0', fontWeight: 600 }}>{asset.Name}</h1>
          </div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onEdit} style={ghostBtnSty()}><Edit2 size={12} /> Edit</button>
            {canDelete && asset.Status !== 'retired' && (
              <button onClick={onDelete} style={ghostBtnSty()}><Trash2 size={12} /> Retire</button>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Details card */}
        <Card>
          <SectionLabel>Details</SectionLabel>
          <Row label="Status"><Badge color={STATUS_COLORS[asset.Status] ?? C.textMuted}>{asset.Status}</Badge></Row>
          <Row label="Assigned To">
            {asset.AssignedToName ?? <span style={{ color: C.textMuted, fontStyle: 'italic' }}>Unassigned</span>}
          </Row>
          <Row label="Serial Number">
            {asset.SerialNumber ?? <span style={{ color: C.textMuted, fontStyle: 'italic' }}>—</span>}
          </Row>
          <Row label="Warranty End">
            {asset.WarrantyEndDate ? new Date(asset.WarrantyEndDate).toLocaleDateString()
              : <span style={{ color: C.textMuted, fontStyle: 'italic' }}>—</span>}
          </Row>
          <Row label="Added to Inventory">
            {new Date(asset.CreatedAt).toLocaleDateString()}
          </Row>
          {asset.Notes && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>Notes</div>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5, color: C.text }}>{asset.Notes}</div>
            </div>
          )}
        </Card>

        {/* Assignment history */}
        <Card>
          <SectionLabel>Assignment History</SectionLabel>
          {loadingHistory ? (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 12 }}>Loading…</div>
          ) : history.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: 13, padding: 12, textAlign: 'center' }}>
              No assignment history.
            </div>
          ) : (
            <div>
              {history.map(h => (
                <div key={h.AssignmentId} style={{
                  padding: '10px 0', borderBottom: `1px solid ${C.border}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{h.DisplayName}</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                      {new Date(h.AssignedAt).toLocaleDateString()}
                      {' → '}
                      {h.UnassignedAt
                        ? new Date(h.UnassignedAt).toLocaleDateString()
                        : <span style={{ color: C.ok, fontWeight: 600 }}>present</span>}
                    </div>
                  </div>
                  {!h.UnassignedAt && <Badge color={C.ok}>Active</Badge>}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Linked tickets */}
        <Card>
          <SectionLabel>Linked Tickets</SectionLabel>
          {linkedTickets.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: 13, padding: '8px 0' }}>No tickets linked to this device.</div>
          ) : (
            <div>
              {linkedTickets.map(t => (
                <div key={t.TicketId} style={{
                  padding: '10px 0', borderBottom: `1px solid ${C.border}`,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      <span style={{ fontFamily: 'monospace', color: C.textMuted, marginRight: 6 }}>{t.TicketKey}</span>
                      {t.Subject}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                      {t.StatusName} · {t.Priority}
                    </div>
                  </div>
                  {canEdit && (
                    <button onClick={() => unlinkTicket(t.TicketId)} disabled={linkBusy} title="Unlink" aria-label="Unlink ticket"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.danger, padding: 2, lineHeight: 0 }}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {canEdit && (
            <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
              <select
                value={ticketPick}
                onFocus={loadAllTickets}
                onChange={e => setTicketPick(e.target.value ? +e.target.value : '')}
                style={{
                  flex: 1, border: `1px solid ${C.border}`, borderRadius: 4, padding: '6px 8px',
                  fontSize: 13, background: C.bg, color: C.text,
                }}>
                <option value="">Link a ticket…</option>
                {allTickets
                  .filter(t => !linkedTickets.some(l => l.TicketId === t.TicketId))
                  .map(t => <option key={t.TicketId} value={t.TicketId}>{t.TicketKey} — {t.Subject}</option>)}
              </select>
              <button onClick={linkTicket} disabled={!ticketPick || linkBusy}
                style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 4, padding: '0 14px', cursor: 'pointer', fontSize: 13 }}>
                Link
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// ============================================================================
// Modal
// ============================================================================
const AssetModal: React.FC<{
  asset: Asset | null;
  users: User[];
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}> = ({ asset, users, onSave, onClose }) => {
  const [assetTag, setAssetTag] = useState(asset?.AssetTag ?? '');
  const [name, setName] = useState(asset?.Name ?? '');
  const [assetType, setAssetType] = useState(asset?.AssetType ?? 'laptop');
  const [serialNumber, setSerialNumber] = useState(asset?.SerialNumber ?? '');
  const [warrantyEndDate, setWarrantyEndDate] = useState(asset?.WarrantyEndDate?.slice(0, 10) ?? '');
  const [assignedUserId, setAssignedUserId] = useState<number | null>(asset?.AssignedUserId ?? null);
  const [status, setStatus] = useState(asset?.Status ?? 'in-use');
  const [notes, setNotes] = useState(asset?.Notes ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!assetTag.trim()) { alert('Asset tag is required'); return; }
    if (!name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      await onSave({
        assetTag: assetTag.trim(),
        name: name.trim(),
        assetType,
        serialNumber: serialNumber.trim() || null,
        warrantyEndDate: warrantyEndDate || null,
        assignedUserId: assignedUserId,
        status,
        notes: notes.trim() || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <div style={modalBackdrop}>
      <div style={{ ...modalCard, maxWidth: 620 }}>
        <div style={modalHeader}>
          <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>
            {asset ? 'Edit Asset' : 'New Asset'}
          </h3>
          <button onClick={onClose} style={modalCloseBtn}><X size={16} /></button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <Field label="Asset Tag">
              <input value={assetTag} onChange={e => setAssetTag(e.target.value)}
                style={inputSty()} placeholder="e.g. LAP-001" />
            </Field>
            <Field label="Name">
              <input value={name} onChange={e => setName(e.target.value)}
                style={inputSty()} placeholder="e.g. Dell Latitude 5440" />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Type">
              <select value={assetType} onChange={e => setAssetType(e.target.value)} style={inputSty()}>
                {ASSET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)} style={inputSty()}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Serial Number">
            <input value={serialNumber} onChange={e => setSerialNumber(e.target.value)}
              style={inputSty()} placeholder="Manufacturer's serial" />
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Assigned To">
              <SearchableSelect
                options={users.map(u => ({ value: u.UserId, label: u.DisplayName }))}
                value={assignedUserId ?? ''}
                onChange={v => setAssignedUserId(v === '' ? null : Number(v))}
                emptyLabel="— unassigned —"
                placeholder="Search people…"
              />
            </Field>
            <Field label="Warranty End Date">
              <input type="date" value={warrantyEndDate} onChange={e => setWarrantyEndDate(e.target.value)}
                style={inputSty()} />
            </Field>
          </div>

          <Field label="Notes">
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              rows={4} style={{ ...inputSty(), resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Configuration details, condition notes, anything useful" />
          </Field>
        </div>

        <div style={modalFooter}>
          <button onClick={onClose} style={ghostBtnSty()}>Cancel</button>
          <button onClick={save} disabled={saving || !assetTag.trim() || !name.trim()} style={{
            background: saving ? '#ccc' : C.primary, color: '#fff', border: 'none',
            padding: '8px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Small reusable bits
// ============================================================================
const Badge: React.FC<{ children: React.ReactNode; color: string }> = ({ children, color }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    color, background: `${color}15`, border: `1px solid ${color}40`, textTransform: 'capitalize',
  }}>{children}</span>
);

const KPICard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: 16,
  }}>
    <div style={{
      fontSize: 11, color: C.textMuted, fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1, marginTop: 6 }}>{value}</div>
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 18,
  }}>{children}</div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 style={{
    margin: '0 0 12px', fontSize: 11, color: C.textMuted, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.4px',
  }}>{children}</h3>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{
    display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8,
    padding: '6px 0', fontSize: 13,
  }}>
    <div style={{ color: C.textMuted }}>{label}</div>
    <div style={{ color: C.text }}>{children}</div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{
      fontSize: 11, fontWeight: 600, color: C.textMuted,
      marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</div>
    {children}
  </div>
);

const daysUntil = (iso: string): number => {
  const d = new Date(iso).getTime();
  const now = Date.now();
  return Math.floor((d - now) / 86400000);
};

// -------- Styles --------
const th = (): React.CSSProperties => ({
  padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px',
});
const td = (): React.CSSProperties => ({ padding: '10px 12px', verticalAlign: 'middle' });
const filterSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
  fontSize: 14, background: C.bg, minWidth: 120,
});
const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
  fontSize: 14, width: '100%', boxSizing: 'border-box', background: C.bg,
});
const ghostBtnSty = (): React.CSSProperties => ({
  background: 'transparent', color: C.text, border: `1px solid ${C.border}`,
  padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
});
const modalBackdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 100, padding: 20,
};
const modalCard: React.CSSProperties = {
  background: C.surface, borderRadius: 8, width: '100%',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
};
const modalHeader: React.CSSProperties = {
  padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const modalFooter: React.CSSProperties = {
  padding: '12px 20px', borderTop: `1px solid ${C.border}`,
  display: 'flex', justifyContent: 'flex-end', gap: 8,
};
const modalCloseBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: 4, color: C.textMuted, display: 'inline-flex',
};

// ============================================================================
// Software catalog manager (Assets → Software tab)
// ============================================================================
